import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Copy, Check } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { apiBaseUrl } from '../../../utils/environment';
import {
  getSsoConnections, getSsoConnection, createSsoConnection, updateSsoConnection, deleteSsoConnection,
} from '../../../services/ssoService';

export const PROTOCOLS = { OIDC: 1, SAML: 2 };
export const PROVIDERS = { ENTRA: 1, ADFS: 2, OTHER: 3 };
export const ENFORCEMENT = { OPTIONAL: 1, REQUIRED: 2 };
export const DOMAIN_STATUS = { PENDING: 1, APPROVED: 2, REJECTED: 3 };

export const DOMAIN_STATUS_COLORS = {
  1: { bg: '#FFF7E6', color: '#B26A00' },
  2: { bg: '#E6F7F1', color: '#0F7B5A' },
  3: { bg: '#FDECEC', color: '#B42318' },
};

// docs.flexbee.app rehberleri (Görev 21) — seçilen sağlayıcı + protokol. Rehber başlıkları (site.json) yalnız harf, boşluk
// ve tire içerir; Redoc etiket bağlantısı boşlukları tireye çevirir (SANDBOX_DOCS_URL '#tag/Sandbox' ile aynı düzen).
export const guideUrl = (provider, protocol) => {
  const name = Number(provider) === PROVIDERS.ADFS ? 'AD-FS' : 'Microsoft-Entra-ID';
  const kind = Number(protocol) === PROTOCOLS.SAML ? 'SAML' : 'OIDC';
  return `https://docs.flexbee.app/#tag/SSO-with-${name}-${kind}`;
};

export const Badge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  margin: 0 4px 4px 0;
  border-radius: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  white-space: nowrap;
`;

const CopyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  & > code {
    flex: 1;
    min-width: 0;
    font-family: monospace;
    font-size: 12px;
    word-break: break-all;
    user-select: all;
  }
`;

export function CopyValue({ label, value, t }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* izin yok */ }
  };
  return (
    <div>
      <strong>{label}</strong>
      <CopyRow>
        <code data-role="sso-copy-value">{value}</code>
        <Button variant="outline" type="button" onClick={copy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? t('sso.copied') : t('sso.copy')}
        </Button>
      </CopyRow>
    </div>
  );
}

export const oidcRedirectUri = () => `${apiBaseUrl()}/v1/auth/sso/oidc/callback`;

export function toFormValues(c) {
  if (!c) return null;
  return {
    displayName: c.displayName,
    protocol: c.protocol,
    providerKind: c.providerKind,
    isEnabled: c.isEnabled,
    enforced: c.enforcement === ENFORCEMENT.REQUIRED,
    applyToSubsidiaries: c.applyToSubsidiaries,
    matchClaim: c.matchClaim || '',
    mobileReauthDays: c.mobileReauthDays,
    oidcAuthority: c.oidcAuthority || '',
    oidcClientId: c.oidcClientId || '',
    oidcClientSecret: '',
    samlIdpMetadataUrl: c.samlIdpMetadataUrl || '',
    samlIdpMetadataXml: '',
    samlIdpEntityId: c.samlIdpEntityId || '',
    samlIdpSsoUrl: c.samlIdpSsoUrl || '',
    samlIdpCertificatePem: '',
    samlWantEncryptedAssertion: c.samlWantEncryptedAssertion,
    domains: (c.domains || []).filter((d) => d.status !== DOMAIN_STATUS.REJECTED).map((d) => d.domain).join('\n'),
  };
}

const blankToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);

export function toRequest(fd) {
  return {
    displayName: (fd.displayName || '').trim(),
    protocol: Number(fd.protocol),
    providerKind: Number(fd.providerKind),
    isEnabled: Boolean(fd.isEnabled),
    enforcement: fd.enforced ? ENFORCEMENT.REQUIRED : ENFORCEMENT.OPTIONAL,
    applyToSubsidiaries: Boolean(fd.applyToSubsidiaries),
    matchClaim: blankToNull(fd.matchClaim),
    mobileReauthDays: Number(fd.mobileReauthDays) || 30,
    oidcAuthority: blankToNull(fd.oidcAuthority),
    oidcClientId: blankToNull(fd.oidcClientId),
    oidcClientSecret: blankToNull(fd.oidcClientSecret),
    samlIdpMetadataUrl: blankToNull(fd.samlIdpMetadataUrl),
    samlIdpMetadataXml: blankToNull(fd.samlIdpMetadataXml),
    samlIdpEntityId: blankToNull(fd.samlIdpEntityId),
    samlIdpSsoUrl: blankToNull(fd.samlIdpSsoUrl),
    samlIdpCertificatePem: blankToNull(fd.samlIdpCertificatePem),
    samlWantEncryptedAssertion: Boolean(fd.samlWantEncryptedAssertion),
    domains: String(fd.domains || '').split(/[\s,;]+/).map((d) => d.trim()).filter(Boolean),
  };
}

const isOidc = (fd) => Number(fd.protocol) !== PROTOCOLS.SAML;
const isSaml = (fd) => Number(fd.protocol) === PROTOCOLS.SAML;

// Sağlayıcı + protokol başına IdP tarafı yönergesi (spec §10.1 bölüm 1) — metinler i18n'de, 5 dilde.
const GUIDES = [
  { key: 'entraOidc', provider: PROVIDERS.ENTRA, protocol: PROTOCOLS.OIDC, when: (fd) => Number(fd.providerKind) !== PROVIDERS.ADFS && isOidc(fd) },
  { key: 'entraSaml', provider: PROVIDERS.ENTRA, protocol: PROTOCOLS.SAML, when: (fd) => Number(fd.providerKind) !== PROVIDERS.ADFS && isSaml(fd) },
  { key: 'adfsOidc', provider: PROVIDERS.ADFS, protocol: PROTOCOLS.OIDC, when: (fd) => Number(fd.providerKind) === PROVIDERS.ADFS && isOidc(fd) },
  { key: 'adfsSaml', provider: PROVIDERS.ADFS, protocol: PROTOCOLS.SAML, when: (fd) => Number(fd.providerKind) === PROVIDERS.ADFS && isSaml(fd) },
];

export function getConnectionSchema(t, { connection, isHolding }) {
  const editing = Boolean(connection);
  const sp = connection?.sp;
  const blockers = connection?.enforceBlockers || [];
  const outdated = connection?.outdatedMobileUserCount || 0;

  return [
    { name: 'displayName', label: t('sso.fields.displayName'), type: 'text', grid: 12, required: true, maxLength: 120 },
    {
      name: 'providerKind', label: t('sso.fields.provider'), type: 'select', grid: 6, defaultValue: PROVIDERS.ENTRA,
      options: [PROVIDERS.ENTRA, PROVIDERS.ADFS, PROVIDERS.OTHER].map((id) => ({ id, name: t(`sso.providers.${id}`) })),
    },
    {
      name: 'protocol', label: t('sso.fields.protocol'), type: 'select', grid: 6, defaultValue: PROTOCOLS.OIDC,
      hidden: editing,
      options: [{ id: PROTOCOLS.OIDC, name: 'OpenID Connect' }, { id: PROTOCOLS.SAML, name: 'SAML 2.0' }],
    },
    {
      name: 'protocolLocked', type: 'notice', grid: 6, hidden: !editing,
      content: `${t('sso.fields.protocol')}: ${Number(connection?.protocol) === PROTOCOLS.SAML ? 'SAML 2.0' : 'OpenID Connect'}`,
    },
    ...GUIDES.map((g) => ({
      name: `guide_${g.key}`, type: 'notice', grid: 12, hidden: (fd) => !g.when(fd),
      content: (
        <div>
          <strong>{t(`sso.guides.${g.key}.title`)}</strong>
          <p style={{ whiteSpace: 'pre-line', margin: '6px 0' }}>{t(`sso.guides.${g.key}.steps`)}</p>
          <a href={guideUrl(g.provider, g.protocol)} target="_blank" rel="noreferrer">{t('sso.guides.open')}</a>
        </div>
      ),
    })),

    // Flexbee bilgileri (kurumun BT ekibine verilecek, kopyalanabilir)
    {
      name: 'spOidc', type: 'notice', grid: 12, hidden: (fd) => !isOidc(fd),
      content: <CopyValue t={t} label={t('sso.sp.redirectUri')} value={sp?.oidcRedirectUri || oidcRedirectUri()} />,
    },
    {
      name: 'spSaml', type: 'notice', grid: 12, hidden: (fd) => !isSaml(fd) || !editing,
      content: sp ? (
        <div>
          <CopyValue t={t} label={t('sso.sp.entityId')} value={sp.samlEntityId} />
          <CopyValue t={t} label={t('sso.sp.acsUrl')} value={sp.samlAcsUrl} />
          <CopyValue t={t} label={t('sso.sp.metadataUrl')} value={sp.samlMetadataUrl} />
          <CopyValue t={t} label={t('sso.sp.signOnUrl')} value={sp.signOnUrl} />
        </div>
      ) : null,
    },
    { name: 'spSamlPending', type: 'notice', grid: 12, hidden: (fd) => !isSaml(fd) || editing, content: t('sso.sp.createdOnSave') },

    // Kimlik sağlayıcı — OIDC
    { name: 'oidcAuthority', label: t('sso.fields.authority'), type: 'text', grid: 12, hidden: (fd) => !isOidc(fd), required: isOidc, description: t('sso.fields.authorityHint') },
    { name: 'oidcClientId', label: t('sso.fields.clientId'), type: 'text', grid: 6, hidden: (fd) => !isOidc(fd), required: isOidc },
    {
      name: 'oidcClientSecret', label: t('sso.fields.clientSecret'), type: 'password', grid: 6, hidden: (fd) => !isOidc(fd),
      required: (fd) => isOidc(fd) && !connection?.hasClientSecret,
      description: connection?.hasClientSecret ? t('sso.fields.clientSecretKeep') : undefined,
    },

    // Kimlik sağlayıcı — SAML
    { name: 'samlIdpMetadataUrl', label: t('sso.fields.metadataUrl'), type: 'text', grid: 12, hidden: (fd) => !isSaml(fd), description: t('sso.fields.metadataUrlHint') },
    { name: 'samlIdpMetadataXml', label: t('sso.fields.metadataXml'), type: 'textarea', rows: 4, grid: 12, hidden: (fd) => !isSaml(fd), description: t('sso.fields.metadataXmlHint') },
    { name: 'samlIdpEntityId', label: t('sso.fields.entityId'), type: 'text', grid: 6, hidden: (fd) => !isSaml(fd) },
    { name: 'samlIdpSsoUrl', label: t('sso.fields.ssoUrl'), type: 'text', grid: 6, hidden: (fd) => !isSaml(fd) },
    { name: 'samlIdpCertificatePem', label: t('sso.fields.certificatePem'), type: 'textarea', rows: 3, grid: 12, hidden: (fd) => !isSaml(fd) },
    {
      name: 'samlCertificates', type: 'notice', grid: 12, hidden: (fd) => !isSaml(fd) || !(connection?.certificates?.length),
      content: (
        <div>
          <strong>{t('sso.fields.certificates')}</strong>
          {(connection?.certificates || []).map((c) => (
            <div key={c.thumbprint} style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {c.thumbprint.slice(0, 16)}… — {t('sso.fields.validUntil', { date: new Date(c.notAfter).toLocaleDateString() })}
            </div>
          ))}
        </div>
      ),
    },
    { name: 'samlWantEncryptedAssertion', label: t('sso.fields.wantEncrypted'), type: 'checkbox', grid: 12, hidden: (fd) => !isSaml(fd) },
    {
      name: 'metadataError', type: 'notice', tone: 'warning', grid: 12, hidden: !connection?.metadataLastError,
      content: t('sso.fields.metadataError'),
    },

    // Eşleştirme + alan adları
    { name: 'matchClaim', label: t('sso.fields.matchClaim'), type: 'text', grid: 12, description: t('sso.fields.matchClaimHint') },
    { name: 'domains', label: t('sso.fields.domains'), type: 'textarea', rows: 3, grid: 12, required: true, description: t('sso.fields.domainsHint') },
    {
      name: 'domainStatuses', type: 'notice', grid: 12, hidden: !(connection?.domains?.length),
      content: (
        <div>
          {(connection?.domains || []).map((d) => (
            <Badge key={d.id} $bg={DOMAIN_STATUS_COLORS[d.status].bg} $color={DOMAIN_STATUS_COLORS[d.status].color} title={d.decisionNote || ''}>
              {d.domain} · {t(`sso.domainStatus.${d.status}`)}
            </Badge>
          ))}
        </div>
      ),
    },

    // Kapsam ve zorunluluk
    { name: 'applyToSubsidiaries', label: t('sso.fields.applyToSubsidiaries'), type: 'checkbox', grid: 12, hidden: !isHolding },
    { name: 'isEnabled', label: t('sso.fields.isEnabled'), type: 'checkbox', grid: 12, defaultValue: true },
    { name: 'enforced', label: t('sso.fields.enforced'), type: 'checkbox', grid: 12 },
    {
      name: 'enforceBlockers', type: 'notice', tone: 'warning', grid: 12,
      hidden: (fd) => !fd.enforced || (editing && blockers.length === 0),
      content: (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {(editing ? blockers : ['sso.enforceBlocked.notTested']).map((b) => <li key={b}>{t(`responseErrors.${b}`)}</li>)}
        </ul>
      ),
    },
    {
      name: 'enforceMobile', type: 'notice', tone: 'warning', grid: 12, hidden: (fd) => !fd.enforced,
      content: outdated > 0 ? t('sso.fields.enforceMobileOutdated', { count: outdated }) : t('sso.fields.enforceMobile'),
    },
    { name: 'mobileReauthDays', label: t('sso.fields.mobileReauthDays'), type: 'number', grid: 6, min: 1, max: 90, step: 1, defaultValue: 30 },
  ];
}

export function useSsoConnections() {
  const [rows, setRows] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [trigger, setTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    getSsoConnections()
      .then((res) => {
        const ok = res?.data?.status;
        setFailed(!ok);
        setRows(ok ? res.data.data || [] : []);
      })
      .finally(() => setFetching(false));
  }, [trigger]);

  const reload = useCallback(() => setTrigger((x) => x + 1), []);

  const loadOne = useCallback(async (id) => {
    const res = await getSsoConnection(id);
    return res?.data?.status ? res.data.data : null;
  }, []);

  const save = useCallback(async (id, request) => {
    try {
      if (id) await updateSsoConnection(id, request);
      else await createSsoConnection(request);
      reload();
      return true;
    } catch {
      return false; // hata toast'ı interceptor'da; çekmece açık kalır
    }
  }, [reload]);

  const remove = useCallback(async (id) => {
    try { await deleteSsoConnection(id); reload(); return true; } catch { return false; }
  }, [reload]);

  return { rows, fetching, failed, reload, loadOne, save, remove };
}
